/**
 * RoomHora — shared motion tokens
 * ------------------------------------------------------------
 * A single source of truth for easing curves, durations, and
 * Framer Motion variants. Keeps animations consistent, tasteful,
 * and performant (transform/opacity only — no layout thrash).
 */

// Custom eases tuned for a premium, real-estate feel.
// Based on Material "Standard" and Apple "Ease Out Expo".
export const ease = {
  standard: [0.32, 0.72, 0, 1],      // smooth, assertive
  gentle:   [0.25, 0.1, 0.25, 1],    // soft, luxurious
  swift:    [0.2, 0, 0, 1],          // fast, crisp
  overshoot:[0.34, 1.56, 0.64, 1],   // gentle pop
};

export const duration = {
  fastest: 0.18,
  fast:    0.28,
  base:    0.42,
  slow:    0.62,
  slower:  0.9,
};

/* ----------------------------- Page transitions ---------------------------- */

export const pageTransition = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -6 },
  transition: { duration: duration.base, ease: ease.standard },
};

/* ------------------------------ Fade / Reveal ------------------------------ */

export const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: duration.base, ease: ease.standard } },
};

export const fadeUpSmall = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: duration.fast, ease: ease.standard } },
};

export const fadeIn = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.base, ease: ease.standard } },
};

export const scaleIn = {
  hidden:  { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1, transition: { duration: duration.base, ease: ease.gentle } },
};

/* ---------------------------- Stagger containers --------------------------- */

export const staggerParent = (stagger = 0.08, delayChildren = 0.05) => ({
  hidden:  { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: stagger, delayChildren },
  },
});

/* --------------------------------- Cards ---------------------------------- */

export const cardLift = {
  rest:  { y: 0, scale: 1 },
  hover: { y: -6, scale: 1.008, transition: { duration: duration.fast, ease: ease.standard } },
  tap:   { scale: 0.992, transition: { duration: 0.12 } },
};

export const imageZoom = {
  rest:  { scale: 1 },
  hover: { scale: 1.07, transition: { duration: duration.slower, ease: ease.gentle } },
};

/* ------------------------------ Micro buttons ------------------------------ */

export const ctaButton = {
  rest:  { scale: 1, y: 0 },
  hover: { scale: 1.02, y: -1, transition: { duration: duration.fast, ease: ease.standard } },
  tap:   { scale: 0.97, transition: { duration: 0.1 } },
};

/* ------------------------------ Hero elements ------------------------------ */

export const heroHeadline = {
  hidden:  { opacity: 0, y: 28, filter: 'blur(6px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)',
             transition: { duration: 0.75, ease: ease.gentle } },
};

export const heroSub = {
  hidden:  { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: duration.slow, ease: ease.gentle, delay: 0.15 } },
};

/* ----------------------------- Viewport helper ----------------------------- */

export const inViewOnce = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.25 },
};
